import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Recording } from '@hotjar/webapp/domains/recordings/types';
import { Text, tokens, Tooltip } from '@hotjar/hotsauce';

import {
  StyledRecordingTileMainContainer,
  StyledRecordingTileMainContainerRow,
  StyledRecordingTileMainContent,
  StyledRecordingTileMainContentOrnament,
  StyledRecordingTileMainSpacer,
  StyledRecordingTileMainURL,
} from './styles';
import {
  getEngagementScoreAsBucket,
  getFrustrationScoreAsBucket,
} from '../../../features/RecordingsList/utils/calculateScore';
import { scoreLabel } from '../../../features/RecordingsList/utils/getScoreLabel';
import { Score } from '../../../features/RecordingsList/cells/ScoreCell';

type RecordingTileMainParams = {
  recording: Recording;
};

export const RecordingTileMain = ({ recording }: RecordingTileMainParams): JSX.Element => {
  const { t } = useTranslation();
  const frustrationScore = getFrustrationScoreAsBucket(recording.frustrationScore) || 0;
  const engagementScore = getEngagementScoreAsBucket(recording.relevanceScore) || 0;

  // duplicated from @webapp/utils/url.ts
  const stripDomain = useCallback((url: string) => {
    try {
      const u = new URL(url);
      return `${u.pathname}${u.search}${u.hash}`;
    } catch (err) {
      // @ts-expect-error fix catch error type
      logError(err);
      return '';
    }
  }, []);

  return (
    <StyledRecordingTileMainContainer>
      {recording.landingPageUrl && recording.exitPageUrl && (
        <StyledRecordingTileMainContentOrnament aria-hidden="true"></StyledRecordingTileMainContentOrnament>
      )}
      <StyledRecordingTileMainContent>
        {recording.landingPageUrl && (
          <StyledRecordingTileMainURL>
            <Text textType="caption">{stripDomain(recording.landingPageUrl)}</Text>
          </StyledRecordingTileMainURL>
        )}
        <StyledRecordingTileMainContainerRow>
          <Tooltip
            text={t(`common:recordings.engagement-score.${scoreLabel[engagementScore]}.title`)}
            placement="left"
          >
            <div aria-hidden="true">
              <Score
                score={engagementScore}
                colorActive={tokens.BRAND_COLORS.cobalt_900}
                colorInactive={tokens.BRAND_COLORS.cobalt_400}
                showLabel={false}
              />
            </div>
          </Tooltip>
          <Text textType="caption" textColor="text_light">
            {t('common:recordings.recordings-overview.widget.cells.page', {
              count: recording.pageCount,
            })}
            <StyledRecordingTileMainSpacer />
          </Text>
          <Text textType="caption" textColor="text_light">
            {t('common:recordings.recordings-overview.widget.cells.action', {
              count: recording.actionCount,
            })}
          </Text>
        </StyledRecordingTileMainContainerRow>
        <StyledRecordingTileMainContainerRow>
          <Tooltip
            text={t(`common:recordings.frustration-score.${scoreLabel[frustrationScore]}.title`)}
            placement="left"
          >
            <div aria-hidden="true">
              <Score
                score={frustrationScore}
                colorActive={tokens.BRAND_COLORS.magenta_800}
                colorInactive={tokens.BRAND_COLORS.magenta_400}
                showLabel={false}
              />
            </div>
          </Tooltip>
          <Text textType="caption" textColor="text_light">
            {t('common:recordings.recordings-overview.widget.cells.rage-click', {
              count: recording.rageClickCount,
            })}
            <StyledRecordingTileMainSpacer />
          </Text>
          <Text textType="caption" textColor="text_light">
            {t('common:recordings.recordings-overview.widget.cells.u-turn', {
              count: recording.uTurnCount,
            })}
          </Text>
        </StyledRecordingTileMainContainerRow>
        {recording.exitPageUrl && (
          <StyledRecordingTileMainURL>
            <Text textType="caption">{stripDomain(recording.exitPageUrl)}</Text>
          </StyledRecordingTileMainURL>
        )}
      </StyledRecordingTileMainContent>
    </StyledRecordingTileMainContainer>
  );
};
